/**
 * Inclusive start/end activity_logs ids for /audits and /reportings.
 * Use the date-sorted table list as-is; keep first id through last id.
 */

import { parseReportLogDateMs } from 'pages/Reporting/reportAudits';

export type LogIdDateHint = {
  id?: string | null,
  sourceId?: string | null,
  date?: string | null,
};

export type AuditsStartEndLogIds = {
  startLogId: string,
  endLogId: string,
};

/** ISO / parseable dates from live activity_logs. */
export const buildLogIdDateMsMap = (
  logs: Array<{ id?: string | null, date?: string | null } | null | undefined> = [],
): Map<string, number> => {
  const dateById = new Map<string, number>();
  logs.forEach((log) => {
    const id = String(log?.id || '').trim();
    const ms = +new Date(log?.date || '');
    if (id && Number.isFinite(ms)) dateById.set(id, ms);
  });
  return dateById;
};

/** Report freeze strings (Today, 14th Aug …) overlay onto live log dates. */
export const mergeLogIdDateMsHints = (
  dateById: Map<string, number>,
  hints: LogIdDateHint[] = [],
): Map<string, number> => {
  hints.forEach((row) => {
    const id = String(row.sourceId || row.id || '').trim();
    const ms = parseReportLogDateMs(row.date);
    if (id && ms != null) dateById.set(id, ms);
  });
  return dateById;
};

/** Freeze hints first, then live activity_logs overwrite — same as /reportings Show logs. */
export const logIdDateMsMapFromLogsAndHints = (
  logs: Array<{ id?: string | null, date?: string | null } | null | undefined> = [],
  hints: LogIdDateHint[] = [],
): Map<string, number> => {
  const dateById = mergeLogIdDateMsHints(new Map<string, number>(), hints);
  buildLogIdDateMsMap(logs).forEach((ms, id) => dateById.set(id, ms));
  return dateById;
};

/** Date only — same-time logs stay in table order (no string-id shuffle). */
const compareLogIdsByDate = (
  a: string,
  b: string,
  dateById: Map<string, number>,
) => {
  const aMs = dateById.get(a) ?? Number.POSITIVE_INFINITY;
  const bMs = dateById.get(b) ?? Number.POSITIVE_INFINITY;
  return aMs - bMs;
};

/** Calendar bounds from start/end log ids (stored even when the table slices by id). */
export const timeRangeFromLogIds = (
  startLogId: string | null | undefined,
  endLogId: string | null | undefined,
  dateById: Map<string, number>,
): { startDate: Date, endDate: Date } | null => {
  const startId = String(startLogId || '').trim();
  const endId = String(endLogId || '').trim();
  const startMs = startId ? dateById.get(startId) : undefined;
  const endMs = endId ? dateById.get(endId) : undefined;
  if (startMs == null && endMs == null) return null;
  const from = startMs ?? endMs as number;
  const to = endMs ?? startMs as number;
  return {
    startDate: new Date(Math.min(from, to)),
    endDate: new Date(Math.max(from, to)),
  };
};

/** First / last ids in the clicked set, ordered by each log's date. */
export const startEndLogIdsFromClicked = (
  sourceIds: string[],
  dateById: Map<string, number>,
): { startLogId?: string, endLogId?: string } => {
  const ids = sourceIds.map((id) => String(id || '').trim()).filter(Boolean);
  if (!ids.length) return {};
  const ranked = [...ids].sort((a, b) => compareLogIdsByDate(a, b, dateById));
  return {
    startLogId: ranked[0],
    endLogId: ranked[ranked.length - 1],
  };
};

/**
 * Pin one bound. The other bound stays unset until chosen — that is a range,
 * not a single-log point. If start is after end, the other bound moves.
 */
export const nextAuditsStartEndLogIds = (
  which: 'start' | 'end',
  rowBoundIds: AuditsStartEndLogIds,
  current: { startLogId?: string | null, endLogId?: string | null },
  dateById: Map<string, number>,
): { startLogId?: string, endLogId?: string } => {
  const rowStart = String(rowBoundIds.startLogId || '').trim();
  const rowEnd = String(rowBoundIds.endLogId || '').trim();
  let start = String(current.startLogId || '').trim();
  let end = String(current.endLogId || '').trim();

  if (which === 'start') {
    start = rowStart;
    if (start && end && compareLogIdsByDate(start, end, dateById) > 0) {
      end = start;
    }
  } else {
    end = rowEnd;
    if (start && end && compareLogIdsByDate(end, start, dateById) < 0) {
      start = end;
    }
  }
  return {
    ...(start ? { startLogId: start } : {}),
    ...(end ? { endLogId: end } : {}),
  };
};

/** Unset one bound. The other stays as start-only or end-only. Both gone → null. */
export const clearAuditsStartEndLogIdBound = (
  which: 'start' | 'end',
  current: { startLogId?: string | null, endLogId?: string | null },
): { startLogId?: string, endLogId?: string } | null => {
  const prevStart = String(current.startLogId || '').trim();
  const prevEnd = String(current.endLogId || '').trim();
  if (!prevStart && !prevEnd) return null;
  if (which === 'start') {
    if (!prevEnd || prevStart === prevEnd) return null;
    return { endLogId: prevEnd };
  }
  if (!prevStart || prevStart === prevEnd) return null;
  return { startLogId: prevStart };
};
